import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Card } from "../../components/UI";
import { VerificationDigestStrip } from "../../components/VerificationDigestCard";
import {
  Sparkles, Megaphone, HandCoins, Landmark, LineChart, ListChecks,
  ArrowRight, Loader2, AlertTriangle, Info, Zap, SlidersHorizontal,
} from "lucide-react";

const PRIMARY_ACTIONS = [
  { to: "/ask-totalreach", icon: Sparkles, label: "Ask TotalReach", desc: "Your AI chief of staff — ask anything about your campaign", testid: "home-card-ask" },
  { to: "/advertise", icon: Megaphone, label: "Advertise", desc: "Tell TotalReach your goal — AI builds the plan, you approve", testid: "home-card-advertise" },
  { to: "/fundraise", icon: HandCoins, label: "Fundraise", desc: "Donation pages, fundraisers, events and Text-to-Give", testid: "home-card-fundraise" },
  { to: "/race", icon: Landmark, label: "Race", desc: "Your election brief — candidates, dates and developments", testid: "home-card-race" },
  { to: "/performance", icon: LineChart, label: "Performance", desc: "How your advertising and fundraising are doing, in plain English", testid: "home-card-performance" },
  { to: "/operations", icon: ListChecks, label: "Operations", desc: "Approvals, tasks, deadlines — everything needing a decision", testid: "home-card-operations" },
];

const PRIORITY_STYLE = {
  critical: { icon: AlertTriangle, cls: "text-rose-300 bg-rose-500/10 border-rose-500/30" },
  important: { icon: Zap, cls: "text-amber-300 bg-amber-400/10 border-amber-400/30" },
  info: { icon: Info, cls: "text-cyan-300 bg-cyan-400/10 border-cyan-400/30" },
};

export default function SimpleHome() {
  const navigate = useNavigate();
  const [cc, setCc] = useState(null);
  const [attention, setAttention] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/political-os/command-center").catch(() => ({ data: null })),
      api.get("/political-os/attention").catch(() => ({ data: { items: [] } })),
      api.get("/war-room/overview").catch(() => ({ data: null })),
    ]).then(([a, b, c]) => {
      setCc(a.data);
      setAttention(b.data?.items || []);
      setOverview(c.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;
  }

  const entity = cc?.entity;
  const nextAction = (attention || [])[0];
  const rest = (attention || []).slice(1);
  const adv = overview?.advertising;
  const fr = overview?.fundraising;

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="simple-home">
      {/* Header: who + race + countdown */}
      <div className="flex flex-wrap items-start justify-between gap-6 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-cyan-300 font-semibold mb-1">Your campaign</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold" data-testid="simple-home-entity-name">
            {entity ? entity.name : "Welcome to TotalReach"}
          </h1>
          {entity && (
            <div className="text-sm text-slate-400 mt-1.5" data-testid="simple-home-race-line">
              {[entity.office, entity.state && `${entity.state}${entity.district ? `-${entity.district}` : ""}`]
                .filter(Boolean).join(" · ") || (entity.entity_type || "").replace(/_/g, " ")}
            </div>
          )}
          {!entity && (
            <div className="mt-3">
              <p className="text-sm text-slate-400 mb-3">Tell TotalReach who you're representing to unlock your campaign workspace.</p>
              <button onClick={() => navigate("/onboarding/political")} data-testid="simple-home-onboard-btn"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold rounded-full px-5 py-2.5 text-sm hover:opacity-90">
                Get set up <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
        {cc?.days_until != null && cc.days_until >= 0 && (
          <div className="text-center px-8 py-4 rounded-2xl border border-cyan-400/20 bg-white/[0.03]" data-testid="simple-home-countdown">
            <div className="font-display text-5xl font-bold bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent">{cc.days_until}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">Days until Election Day</div>
          </div>
        )}
      </div>

      {/* Verification momentum strip */}
      {entity && <VerificationDigestStrip />}

      {/* Next best action */}
      {nextAction && (
        <button onClick={() => navigate(nextAction.link || "/operations")} data-testid="next-best-action"
          className="w-full text-left mb-8 rounded-2xl border border-cyan-400/25 bg-gradient-to-r from-cyan-400/10 to-transparent p-6 hover:border-cyan-400/50 transition-colors group">
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold mb-2 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> What should I do next?
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-display text-xl font-bold">{nextAction.title}</div>
              <div className="text-sm text-slate-400 mt-1">{nextAction.detail}</div>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-cyan-300 transition-colors shrink-0" />
          </div>
        </button>
      )}

      {/* Six primary actions */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-10" data-testid="simple-home-actions">
        {PRIMARY_ACTIONS.map((a) => (
          <button key={a.to} onClick={() => navigate(a.to)} data-testid={a.testid}
            className="group text-left rounded-2xl border border-white/10 bg-white/[0.02] hover:border-cyan-400/40 hover:bg-cyan-400/[0.04] transition-colors p-5">
            <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center mb-3">
              <a.icon className="w-5 h-5 text-cyan-300" />
            </div>
            <div className="font-display text-lg font-bold">{a.label}</div>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">{a.desc}</p>
          </button>
        ))}
      </div>

      {/* AI Daily Brief + Recommendations */}
      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        <DailyBriefCard />
        <RecommendationsCard navigate={navigate} />
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Attention feed */}
        <Card className="lg:col-span-3" data-testid="simple-attention-feed">
          <h3 className="font-display text-lg font-bold mb-4 flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-cyan-300" /> What needs your attention
          </h3>
          {rest.length === 0 && !nextAction ? (
            <p className="text-sm text-slate-500">All clear — nothing waiting on you right now.</p>
          ) : (
            <div className="space-y-2">
              {(nextAction ? [nextAction, ...rest] : rest).map((i, idx) => {
                const s = PRIORITY_STYLE[i.priority] || PRIORITY_STYLE.info;
                return (
                  <button key={idx} onClick={() => navigate(i.link || "/operations")} data-testid={`simple-attention-${idx}`}
                    className="w-full text-left flex items-center gap-3 p-3 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] transition-colors">
                    <span className={`w-7 h-7 rounded-lg border flex items-center justify-center shrink-0 ${s.cls}`}>
                      <s.icon className="w-3.5 h-3.5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm text-white truncate">{i.title}</span>
                      <span className="block text-[11px] text-slate-500 truncate">{i.detail}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        {/* Snapshot */}
        <Card className="lg:col-span-2" data-testid="simple-home-snapshot">
          <h3 className="font-display text-lg font-bold mb-4 flex items-center gap-2">
            <LineChart className="w-4 h-4 text-cyan-300" /> Campaign snapshot
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Ad budget" value={adv ? `$${Number(adv.total_budget || 0).toLocaleString()}` : "—"} />
            <Stat label="Active campaigns" value={adv ? `${adv.active_campaigns} of ${adv.total_campaigns}` : "—"} />
            <Stat label="Funds raised" value={fr ? `$${Number(fr.total_raised || 0).toLocaleString()}` : "—"} />
            <Stat label="Donors" value={fr ? String(fr.donors || 0) : "—"} />
          </div>
          <button onClick={() => navigate("/performance")} data-testid="simple-home-open-performance"
            className="mt-4 text-xs text-cyan-300 hover:text-cyan-100 inline-flex items-center gap-1">
            See full performance <ArrowRight className="w-3 h-3" />
          </button>
        </Card>
      </div>

      <div className="mt-8 text-center">
        <button onClick={() => navigate("/dashboard/advanced")} data-testid="open-advanced-dashboard"
          className="text-xs text-slate-500 hover:text-cyan-300 inline-flex items-center gap-1.5 transition-colors">
          <SlidersHorizontal className="w-3.5 h-3.5" /> Open Mission Control — Advanced
        </button>
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
    <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className="font-display text-xl font-bold mt-0.5">{value}</div>
  </div>
);

const DailyBriefCard = () => {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailOn, setEmailOn] = useState(null);
  const [togglingEmail, setTogglingEmail] = useState(false);
  const load = (refresh) => {
    setLoading(true);
    api.get(`/simple/daily-brief${refresh ? "?refresh=true" : ""}`)
      .then((r) => setBrief(r.data))
      .catch(() => setBrief(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load(false);
    api.get("/simple/brief-subscription").then((r) => setEmailOn(!!r.data?.enabled)).catch(() => {});
  }, []);
  const toggleEmail = async () => {
    setTogglingEmail(true);
    const next = !emailOn;
    try {
      await api.put("/simple/brief-subscription", { enabled: next });
      setEmailOn(next);
      toast.success(next ? "Morning brief email on — it'll arrive each morning" : "Morning brief email off");
    } catch { toast.error("Could not update email setting"); }
    finally { setTogglingEmail(false); }
  };
  return (
    <Card data-testid="daily-brief-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg font-bold flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-cyan-300" /> Today&apos;s brief
        </h3>
        <div className="flex items-center gap-3">
          {emailOn !== null && (
            <button onClick={toggleEmail} disabled={togglingEmail} data-testid="daily-brief-email-toggle"
              title={emailOn ? "You'll get this brief by email each morning" : "Turn on the morning email brief"}
              className={`text-[10px] uppercase tracking-widest transition-colors inline-flex items-center gap-1 ${emailOn ? "text-emerald-300 hover:text-emerald-100" : "text-slate-500 hover:text-cyan-300"}`}>
              {emailOn ? "✓ Morning email on" : "Email me each morning"}
            </button>
          )}
          {brief && !loading && (
            <button onClick={() => load(true)} data-testid="daily-brief-refresh"
              className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-cyan-300 transition-colors">
              Refresh
            </button>
          )}
        </div>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-4" data-testid="daily-brief-loading">
          <Loader2 className="w-4 h-4 animate-spin text-cyan-300" /> TotalReach is writing your brief…
        </div>
      ) : brief ? (
        <div className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap max-h-72 overflow-y-auto" data-testid="daily-brief-text">
          {brief.brief}
        </div>
      ) : (
        <p className="text-sm text-slate-500">Brief unavailable right now — try again shortly.</p>
      )}
    </Card>
  );
};

const RecommendationsCard = ({ navigate }) => {
  const [recs, setRecs] = useState(null);
  const [deciding, setDeciding] = useState("");
  useEffect(() => {
    api.post("/simple/recommendations/generate").catch(() => {})
      .finally(() => {
        api.get("/simple/recommendations")
          .then((r) => setRecs(r.data.recommendations || []))
          .catch(() => setRecs([]));
      });
  }, []);
  const decide = async (rec, action) => {
    setDeciding(rec.id + action);
    try {
      const { data } = await api.post(`/simple/recommendations/${rec.id}/decision`, { action });
      setRecs((prev) => prev.filter((r) => r.id !== rec.id));
      if (action === "approve") {
        toast.success(rec.action?.type === "review" ? "Opening it for you" : "Done — TotalReach handled it");
        if (data.link) navigate(data.link);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not apply that");
    } finally { setDeciding(""); }
  };
  return (
    <Card data-testid="recommendations-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg font-bold flex items-center gap-2">
          <Zap className="w-4 h-4 text-cyan-300" /> AI recommends
        </h3>
        <button onClick={() => navigate("/autopilot")} data-testid="open-autopilot-settings"
          className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-cyan-300 transition-colors">
          Autopilot settings
        </button>
      </div>
      {recs === null ? (
        <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-cyan-300" /></div>
      ) : recs.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="recommendations-empty">No open recommendations — TotalReach will surface new ones as your campaigns run.</p>
      ) : (
        <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
          {recs.map((r) => (
            <div key={r.id} className="rounded-xl border border-white/5 bg-white/[0.02] p-3.5" data-testid={`rec-card-${r.kind}`}>
              <div className="text-sm font-semibold">{r.title}</div>
              {r.campaign_name && <div className="text-[10px] text-cyan-300/80 mt-0.5">{r.campaign_name}</div>}
              <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">{r.reason}</p>
              <p className="text-[10px] text-slate-500 mt-1 italic">{r.expected_effect}</p>
              <div className="flex gap-2 mt-2.5">
                <button onClick={() => decide(r, "approve")} disabled={!!deciding} data-testid={`rec-approve-${r.id}`}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5">
                  {deciding === r.id + "approve" ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                  {r.action?.type === "review" ? "Review" : "Approve"}
                </button>
                <button onClick={() => navigate("/ask-totalreach")} data-testid={`rec-ask-${r.id}`}
                  className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors">
                  Ask AI
                </button>
                <button onClick={() => decide(r, "dismiss")} disabled={!!deciding} data-testid={`rec-dismiss-${r.id}`}
                  className="text-xs px-3 py-1.5 rounded-full text-slate-500 hover:text-rose-300 transition-colors disabled:opacity-50">
                  Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
